/**
 * Shared helpers for the admin Requests screens.
 *
 * Centralises:
 *  - the 4 request types and their admin labels
 *  - the short machine->human label maps for select values (so a detail view
 *    shows “Training collaboration” instead of the raw `training` key)
 *  - a single `formatDate` helper (admin UI is English-only, LTR)
 */

export const REQUEST_TYPES = ["partnership", "workshop", "contact", "volunteer", "support", "v2_feedback"];

export const TYPE_LABELS = {
  partnership: "Partnership Requests",
  workshop: "Workshop Hosting Requests",
  contact: "General Contact Requests",
  volunteer: "Volunteer Requests",
  support: "Support Requests",
  v2_feedback: "V2 Feedback",
};

export const STATUS_LABELS = {
  new: "New",
  in_progress: "In progress",
  done: "Done",
};

export const STATUS_OPTIONS = ["new", "in_progress", "done"];

export const REPLY_METHOD_LABELS = {
  whatsapp: "WhatsApp",
  phone: "Phone call",
  email: "Email",
  "": "—",
};

/** Maps stored select values -> English human labels per form type. */
export const FIELD_VALUE_LABELS = {
  partnership: {
    collaboration_type: {
      training: "Training collaboration",
      media: "Media collaboration",
      technical: "Technical collaboration",
      community: "Community collaboration",
      long_term: "Long-term partnership",
      other: "Other",
    },
  },
  workshop: {
    audience: {
      university_students: "University students",
      school_students: "School students",
      teachers_trainers: "Teachers or trainers",
      researchers: "Researchers",
      general_public: "General public",
      organisation_or_team: "Organisation or work team",
      general_introductory: "General introductory workshop",
      applied_workshop: "Practical workshop",
      training_series: "Training series",
    },
  },
  contact: {
    subject: {
      general_inquiry: "General inquiry",
      information_request: "Information request",
      suggestion_note: "Suggestion or note",
      website_issue: "Website issue",
      workshop_question: "Workshop question",
      tools_question: "Tools question",
      other: "Other",
    },
  },
  volunteer: {
    contribution_area: {
      training: "Training",
      content_creation: "Content creation",
      design: "Design",
      programming: "Programming",
      translation: "Translation",
      coordination: "Coordination and organisation",
      partnerships_outreach: "Partnerships and outreach",
      community_support: "Community support",
      other: "Other",
    },
  },
  // Phase 24 — SAS V2 feedback. Keeps parity with the frontend spec's
  // `V2_FEEDBACK_FORM.fields[…].options[].key` values.
  v2_feedback: {
    suggestion_category: {
      new_tool: "New AI tool",
      website_feature: "Website feature",
      article: "Scientific / practical article",
      syria_access: "Syria access or VPN issue",
      partnership_or_community: "Partnership or community idea",
      bug_or_improvement: "Bug or improvement",
      other: "Other",
    },
    // `who_helps` is a multiselect (list of keys). humanizeField still
    // works on each individual key when rendered on the admin detail view.
    who_helps: {
      students: "Students",
      researchers: "Researchers",
      teachers: "Teachers",
      developers: "Developers",
      general_public: "General public",
      organizations: "Organizations",
    },
  },
};

/** Maps stored field names -> pretty English labels per form type. */
export const FIELD_LABELS = {
  partnership: {
    full_name: "Full name",
    organisation_name: "Organisation or institution",
    email: "Email address",
    collaboration_type: "Type of collaboration",
    description: "Description",
  },
  workshop: {
    full_name: "Full name",
    host_entity: "Host institution or organisation",
    phone_or_whatsapp: "Phone or WhatsApp",
    audience: "Target audience / workshop type",
    notes: "Additional notes",
  },
  contact: {
    full_name: "Full name",
    phone_or_email: "Phone or email",
    subject: "Subject",
    message: "Message",
    city_or_entity: "City or organisation",
  },
  volunteer: {
    full_name: "Full name",
    phone_or_email: "Phone or email",
    contribution_area: "Area of contribution",
    bio: "Experience / interest",
    city_or_participation: "City or participation mode",
  },
  v2_feedback: {
    suggestion_category: "Suggestion category",
    title: "Idea title",
    description: "Idea description",
    who_helps: "Who benefits",
    contact_name: "Name (optional)",
    contact_reach: "Email or phone (optional)",
  },
};

export function humanizeField(requestType, fieldName, value) {
  if (value == null || value === "") return "—";
  const map = FIELD_VALUE_LABELS[requestType] || {};
  const fieldMap = map[fieldName];
  if (fieldMap && fieldMap[value]) return fieldMap[value];
  return value;
}

export function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function submitterName(doc) {
  if (!doc || !doc.fields) return "—";
  return (
    doc.fields.full_name ||
    doc.fields.contact_name ||
    (doc.request_type === "v2_feedback" ? "Anonymous" : "—")
  );
}

export function submitterContact(doc) {
  const f = (doc && doc.fields) || {};
  return (
    f.email ||
    f.phone_or_whatsapp ||
    f.phone_or_email ||
    f.contact_reach ||
    "—"
  );
}
